import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { supabase } from '../../../../lib/supabase';

export type WaitlistRow = {
    id: string
    name: string
    class: string
    day_of_week: number
    school_id: string
    registration_period_id: string
    semester_id: string
    first_choice: string | null
    second_choice: string | null
    third_choice: string | null
    current_enrollment: string | null
    current_enrollment_name?: string
}

export type GetWaitlistRowsParams = {
    schoolId: string
    registrationPeriodId: string
    semesterId: string
    day?: number | null
    search?: string
    onlyUnassigned?: boolean
}

export type EnrollWaitlistStudentParams = {
    studentId: string
    toCourseId: string
    semesterId: string
    dayId?: number
    fromCourseId?: string | null
}

export const waitlistApi = createApi({
    reducerPath: 'waitlistApi',
    baseQuery: fetchBaseQuery({ baseUrl: '/' }), // Dummy base query, using Supabase client directly
    tagTypes: ['Waitlist', 'Enrollments'],
    endpoints: (builder) => ({
        getWaitlistRows: builder.query<WaitlistRow[], GetWaitlistRowsParams>({
            queryFn: async (params) => {
                const { schoolId, registrationPeriodId, semesterId, day, search, onlyUnassigned } = params
                let q = supabase
                    .from('vw_react_registration_period_students_by_day' as any)
                    .select('id,name,class,day_of_week,school_id,registration_period_id,semester_id,first_choice,second_choice,third_choice,current_enrollment')
                    .eq('school_id', schoolId)
                    .eq('registration_period_id', registrationPeriodId)
                    .eq('semester_id', semesterId)

                if (onlyUnassigned) q = q.is('current_enrollment', null)
                if (typeof day === 'number') q = q.eq('day_of_week', day)
                if (search && search.trim().length >= 2) {
                    const s = search.trim()
                    q = q.ilike('name', `%${s}%`)
                }

                const { data, error } = await q.order('day_of_week', { ascending: true }).order('class', { ascending: true }).order('name', { ascending: true })

                if (error) return { error: { status: 500, data: error } }
                return { data: (data || []) as WaitlistRow[] }
            },
            providesTags: ['Waitlist'],
        }),
        getCourseWindows: builder.query<{ window_id: string; day_id: number; school_id?: string }[], { registrationPeriodId: string; courseId: string; schoolId?: string }>({
            queryFn: async ({ registrationPeriodId, courseId, schoolId }) => {
                let q = supabase
                    .from('vw_react_wuensche_course_windows_schedules' as any)
                    .select('window_id, day_id, school_id')
                    .eq('registration_period_id', registrationPeriodId)
                    .eq('course_id', courseId)

                if (schoolId) {
                    q = q.eq('school_id', schoolId)
                }

                const { data, error } = await q
                if (error) return { error: { status: 500, data: error } }
                return { data: (data || []) as any[] }
            }
        }),
        getCourseListByIds: builder.query<{ id: string; name: string }[], string[]>({
            queryFn: async (ids) => {
                if (ids.length === 0) return { data: [] }
                const { data, error } = await supabase
                    .from('course_list')
                    .select('id,name')
                    .in('id', ids)

                if (error) return { error: { status: 500, data: error } }
                return { data: (data || []) as any[] }
            }
        }),
        getSameDayCourses: builder.query<{ id: string; name: string }[], { registrationPeriodId: string; schoolId: string; dayId: number }>({
            queryFn: async ({ registrationPeriodId, schoolId, dayId }) => {
                const { data, error } = await supabase
                    .from('vw_react_wuensche_course_windows_schedules' as any)
                    .select('course_id')
                    .eq('registration_period_id', registrationPeriodId)
                    .eq('school_id', schoolId)
                    .eq('day_id', dayId)

                if (error) return { error: { status: 500, data: error } }

                const ids = Array.from(new Set((data || []).map((r: any) => String(r.course_id)).filter(Boolean)))
                if (ids.length === 0) return { data: [] }

                const { data: names, error: nerr } = await supabase
                    .from('course_list')
                    .select('id,name')
                    .in('id', ids)

                if (nerr) return { error: { status: 500, data: nerr } }
                return { data: (names || []).map((r: any) => ({ id: String(r.id), name: String(r.name || r.id) })) }
            }
        }),
        enrollWaitlistStudent: builder.mutation<void, EnrollWaitlistStudentParams>({
            queryFn: async ({ studentId, toCourseId, semesterId, dayId, fromCourseId }) => {
                const payload: any = {
                    p_student_id: studentId,
                    p_to_course_id: toCourseId,
                    p_semester_id: semesterId,
                }
                if (dayId) payload.p_day_id = dayId
                if (fromCourseId) payload.p_from_course_id = fromCourseId

                const { error } = await supabase.rpc('rpc_nachruecken_enroll' as any, payload)

                if (error) return { error: { status: 500, data: error } }
                return { data: undefined }
            },
            invalidatesTags: ['Waitlist', 'Enrollments']
        })
    }),
});

export const { useGetWaitlistRowsQuery, useEnrollWaitlistStudentMutation, useGetCourseWindowsQuery, useGetCourseListByIdsQuery, useGetSameDayCoursesQuery } = waitlistApi;
